// tileMap — Web Mercator arithmetic for a STATIC, fitted slippy map.
//
// The owner asked for a map on Right now: "shows workers location, by default
// zoom in to see all workers". This file is the half of that answer which has
// no DOM in it — where a coordinate lands in a box of pixels, which map tiles
// cover that box, and what zoom makes every point fit at once.
//
// ── Why there is no map LIBRARY under this ─────────────────────────────────
//
// Leaflet was the obvious candidate and was deliberately not taken. Four
// reasons, in the order they matter here:
//
//   1. A dependency in `client/package.json` is Tier C in CLAUDE.md's
//      auto-merge table — a human review at any size, for every future change
//      that touches it. The arithmetic below is ~120 lines and testable in
//      jsdom, which is a smaller thing to own than a vendored map engine.
//   2. An INTERACTIVE map embedded mid-page hijacks vertical touch drags on a
//      phone. Right now is a screen you scroll. A map that eats the scroll
//      gesture is exactly the "browser stuffed into an app" complaint
//      docs/APP_REBUILD.md was written about, in a new place.
//   3. Leaflet ships its own stylesheet full of fixed `px` and its own
//      z-index scale, neither of which this repo's guards
//      (cssNarrowFloors.test.js, zIndexScale.test.js) can see inside
//      node_modules.
//   4. What was actually asked for is a DEFAULT VIEW that fits everybody —
//      not panning and not zooming. `fitView` below is that, exactly, and a
//      view that cannot be moved cannot be moved to somewhere misleading.
//
// The cost of that choice, stated: there is no pan and no zoom. A pin is a
// tap-through to the full external map instead, which is the thing a
// dispatcher actually wants next anyway.
//
// ── What leaves the browser ────────────────────────────────────────────────
//
// Tile images are fetched from openstreetmap.org by the DISPATCHER's browser.
// That request carries the tile's coordinates — i.e. roughly which part of
// Connecticut is on screen — and nothing else: no name, no user id, no job.
// It is strictly less than the app already discloses when somebody taps
// "Open in maps", which hands Google an exact fix. Attribution is required by
// the ODbL and the component renders it.

/** One tile is 256 CSS pixels square. This is the tile scheme's unit, not a layout width. */
export const TILE_SIZE = 256;

/** Web Mercator cannot represent the poles; this is the standard cut-off. */
export const MAX_LAT = 85.05112878;

export const MIN_ZOOM = 1;
/** OSM serves to 19. 18 leaves a step of headroom and is already building-level. */
export const MAX_ZOOM = 18;
/**
 * The zoom for a view with nothing to span — one pin, or several on the same
 * spot. Street level: close enough to name the road, wide enough that a fix
 * which is a block out still shows the right neighbourhood.
 */
export const POINT_ZOOM = 16;

/** The default basemap. No API key, no account, no paid tier. */
export const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
export const OSM_ATTRIBUTION = 'OpenStreetMap contributors';
export const OSM_COPYRIGHT_URL = 'https://www.openstreetmap.org/copyright';

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * A lat/lng to absolute world pixels at `zoom`.
 *
 * The sine form rather than tan+sec: at the equator `Math.tan(0) + 1/Math.cos(0)`
 * is fine, but the identity is cheaper and has no singularity to reason about,
 * and latitude is clamped first so a corrupt row cannot produce Infinity and
 * then NaN pixel offsets — which render as an element pinned to the top-left
 * corner rather than as an error anybody would notice.
 */
export function project(lat, lng, zoom) {
  const scale = TILE_SIZE * Math.pow(2, zoom);
  const la = clamp(isNum(lat) ? lat : 0, -MAX_LAT, MAX_LAT);
  const lo = clamp(isNum(lng) ? lng : 0, -180, 180);
  const sin = Math.sin((la * Math.PI) / 180);
  return {
    x: ((lo + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale,
  };
}

/**
 * The smallest box containing every point, or null when there are none.
 *
 * Points with a non-finite coordinate are DROPPED rather than clamped: a row
 * whose latitude arrived as a string is not a place, and including it would
 * silently drag the view to 0,0 in the Atlantic — the classic "null island"
 * failure, which looks like a working map of the wrong thing.
 */
export function boundsOf(points) {
  const usable = (points || []).filter((p) => p && isNum(p.lat) && isNum(p.lng));
  if (!usable.length) return null;
  let north = usable[0].lat;
  let south = usable[0].lat;
  let east = usable[0].lng;
  let west = usable[0].lng;
  for (const p of usable) {
    if (p.lat > north) north = p.lat;
    if (p.lat < south) south = p.lat;
    if (p.lng > east) east = p.lng;
    if (p.lng < west) west = p.lng;
  }
  return { north, south, east, west };
}

/**
 * The largest whole zoom at which `bounds` fits inside width x height.
 *
 * Projected spans scale linearly with 2^zoom, so the span at zoom 0 answers
 * every zoom: the bounds fit when `span0 * 2^z <= available`, i.e.
 * `z <= log2(available / span0)`. Floor it, because a fractional zoom would
 * mean fractional tiles.
 *
 * A zero span on one axis (everyone on the same street, or a single pin)
 * constrains nothing on that axis and is skipped — not treated as "fits at
 * every zoom and therefore MAX_ZOOM", which is how a two-pin view of one
 * street ends up showing a single roof.
 */
export function fitZoom(bounds, { width, height, padding = 0, maxZoom = MAX_ZOOM } = {}) {
  const w = Math.max(1, (width || 0) - padding * 2);
  const h = Math.max(1, (height || 0) - padding * 2);
  if (!bounds) return clamp(POINT_ZOOM, MIN_ZOOM, maxZoom);

  const nw = project(bounds.north, bounds.west, 0);
  const se = project(bounds.south, bounds.east, 0);
  const dx = Math.abs(se.x - nw.x);
  const dy = Math.abs(se.y - nw.y);
  if (dx <= 0 && dy <= 0) return clamp(POINT_ZOOM, MIN_ZOOM, maxZoom);

  const zx = dx > 0 ? Math.log2(w / dx) : Infinity;
  const zy = dy > 0 ? Math.log2(h / dy) : Infinity;
  return clamp(Math.floor(Math.min(zx, zy)), MIN_ZOOM, maxZoom);
}

/**
 * The whole view: which zoom, and where the top-left of the box sits in world
 * pixels. Everything else on screen is measured from `originX/originY`.
 *
 * Returns null for a box with no size or a set with no usable point, and the
 * component renders a sentence for that rather than an empty frame. A map that
 * draws nothing and says nothing is the grey box this feature must not be.
 *
 * The centre is the midpoint of the PROJECTED corners, not the midpoint of the
 * latitudes. Mercator is not linear in latitude, so averaging degrees drifts —
 * and the size of that drift is worth stating honestly rather than implying,
 * because it argues for correctness and not for urgency. Measured at the zoom
 * each span actually fits at:
 *
 *   Hartford → New Haven   0.8px      the ordinary day on this screen
 *   Hartford → Boston      0.3px
 *   Connecticut → Florida   10px
 *   the continental US      14px
 *
 * So on the spans a landscaping company in Connecticut produces it is
 * sub-pixel and nobody would ever see it. It is written the correct way
 * anyway, because the correct way is not harder, and because the one day it
 * matters is the day somebody's phone reports a fix a thousand miles out —
 * which is exactly the cached-position failure this feature has to survive.
 */
export function fitView(points, { width, height, padding = 24, maxZoom = MAX_ZOOM } = {}) {
  if (!isNum(width) || !isNum(height) || width <= 0 || height <= 0) return null;
  const bounds = boundsOf(points);
  if (!bounds) return null;

  const zoom = fitZoom(bounds, { width, height, padding, maxZoom });
  const nw = project(bounds.north, bounds.west, zoom);
  const se = project(bounds.south, bounds.east, zoom);
  const centreX = (nw.x + se.x) / 2;
  const centreY = (nw.y + se.y) / 2;

  return {
    zoom,
    bounds,
    width,
    height,
    originX: centreX - width / 2,
    originY: centreY - height / 2,
  };
}

/**
 * Where a point lands inside the view's box, in CSS pixels from its top-left.
 * May be negative or past the edge; the caller decides what to do with a pin
 * that is outside, and `fitView`'s padding is what normally prevents it.
 */
export function pinAt(view, point) {
  if (!view || !point) return null;
  const { x, y } = project(point.lat, point.lng, view.zoom);
  return { left: x - view.originX, top: y - view.originY };
}

/**
 * Every tile needed to cover the view, with the offset to place each one at.
 *
 * `y` outside the world is dropped — at low zoom the box can be taller than
 * the map, and a request for tile -1 is a 404 per pin-drop of a screen. `x`
 * WRAPS, because longitude does: a view straddling the date line needs tile
 * 0 to the right of tile 2^z-1, and the URL wants the wrapped number while the
 * position wants the unwrapped one. Not reachable from Connecticut, and
 * written correctly anyway because the failure is invisible until it isn't.
 */
export function tilesFor(view, { url = OSM_TILE_URL } = {}) {
  if (!view) return [];
  const n = Math.pow(2, view.zoom);
  const firstX = Math.floor(view.originX / TILE_SIZE);
  const lastX = Math.floor((view.originX + view.width - 1) / TILE_SIZE);
  const firstY = Math.floor(view.originY / TILE_SIZE);
  const lastY = Math.floor((view.originY + view.height - 1) / TILE_SIZE);

  const out = [];
  for (let y = firstY; y <= lastY; y++) {
    if (y < 0 || y >= n) continue;
    for (let x = firstX; x <= lastX; x++) {
      const wrapped = ((x % n) + n) % n;
      out.push({
        key: `${view.zoom}/${wrapped}/${y}@${x}`,
        x: wrapped,
        y,
        z: view.zoom,
        left: x * TILE_SIZE - view.originX,
        top: y * TILE_SIZE - view.originY,
        url: tileUrl(wrapped, y, view.zoom, url),
      });
    }
  }
  return out;
}

/** One tile's URL from a `{z}/{x}/{y}` template. */
export function tileUrl(x, y, z, template = OSM_TILE_URL) {
  return String(template)
    .replace('{z}', String(z))
    .replace('{x}', String(x))
    .replace('{y}', String(y));
}

/**
 * How many metres one screen pixel covers at this latitude and zoom.
 *
 * This is what turns a GPS accuracy figure in metres into a circle somebody
 * can see. It exists so an accuracy radius is DRAWN TO SCALE or not drawn at
 * all — a fixed-size halo that does not mean anything is worse than no halo,
 * because it looks like it means something.
 */
export function metresPerPixel(lat, zoom) {
  const la = clamp(isNum(lat) ? lat : 0, -MAX_LAT, MAX_LAT);
  return (156543.03392 * Math.cos((la * Math.PI) / 180)) / Math.pow(2, zoom);
}
